import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { Trash2, Plus, ChevronDown, Type, Hash, Calendar, ToggleLeft, List } from 'lucide-react';
import {
  getOperatorsForFieldType,
  operatorRequiresValue,
  operatorRequiresMultipleValues,
  operatorSupportsMultiSelect,
  splitCommaSeparatedFilterValue,
  OPERATORS,
} from './operators';
import FilterService from './FilterService';

const FIELD_TYPE_ICON = {
  text: Type,
  number: Hash,
  date: Calendar,
  datetime: Calendar,
  boolean: ToggleLeft,
  foreign_key: List,
  enum: List,
  dropdown: List,
};

/** MUI sx overrides so Autocomplete / DatePicker inputs match the `.ems-fm-input` look. */
const autocompleteInputSx = {
  '& .MuiOutlinedInput-root': {
    padding: '3px 8px',
    minHeight: 42,
    borderRadius: '10px',
    backgroundColor: 'var(--ems-surface)',
    fontFamily: 'inherit',
    fontSize: 14,
    '& fieldset': { borderColor: 'var(--ems-border-strong)' },
    '&:hover fieldset': { borderColor: 'var(--ems-border-strong)' },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--ems-primary)',
      borderWidth: '1px',
      boxShadow: '0 0 0 3px var(--ems-ring)',
    },
  },
  '& .MuiInputBase-input': { color: 'var(--ems-text)' },
};

const dateFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 42,
    borderRadius: '10px',
    backgroundColor: 'var(--ems-surface)',
    fontFamily: 'inherit',
    fontSize: 14,
    color: 'var(--ems-text)',
    '& fieldset': { borderColor: 'var(--ems-border-strong)' },
    '&:hover fieldset': { borderColor: 'var(--ems-border-strong)' },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--ems-primary)',
      borderWidth: '1px',
      boxShadow: '0 0 0 3px var(--ems-ring)',
    },
  },
};

// The filters modal backdrop/panel sit at z-index 10010+ (see ems-design.css .ems-flt-*),
// well above MUI's default popper z-index (1300) — without this override the
// Autocomplete/DatePicker dropdowns render invisibly behind the modal.
const popperSlotProps = { popper: { sx: { zIndex: 10100 } } };

const chipSx = {
  height: 26,
  borderRadius: '99px',
  backgroundColor: 'var(--ems-surface-2)',
  border: '1px solid var(--ems-border-strong)',
  color: 'var(--ems-text)',
  fontWeight: 600,
  fontSize: 12,
};

/**
 * Helper: Extract option label/value
 */
const getOptionValue = (option) => (typeof option === 'object' ? option.value : option);
const getOptionLabel = (option) => (typeof option === 'object' ? option.label : String(option));

/** Normalize stored filter value(s) — split comma-joined FK ids from URL. */
const normalizeMultiSelectStoredValues = (value) => {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) {
    return value.flatMap((v) => splitCommaSeparatedFilterValue(v));
  }
  return splitCommaSeparatedFilterValue(value);
};

/** Resolve display label for a stored id when lookup options are not loaded yet. */
const getStoredValueLabel = (filter, stored, index) => {
  if (filter.valueLabel == null || filter.valueLabel === '') return null;
  const labels = Array.isArray(filter.valueLabel)
    ? filter.valueLabel
    : String(filter.valueLabel)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
  if (labels[index]) return labels[index];
  if (labels.length === 1 && index === 0) return labels[0];
  return null;
};

/**
 * Helper: Get normalized array value
 */
const getArrayValue = (value, defaultValue = [null, null]) =>
  Array.isArray(value) ? value : defaultValue;

/**
 * FilterRow Component
 * Renders a single filter row: read-only field pill, operator select, value control(s), delete
 */
function FilterRow({ filter, metadataFields, lookupContextParams = {}, onChange, onDelete, index }) {
  const [lookupOptions, setLookupOptions] = useState([]);
  const [loadingLookup, setLoadingLookup] = useState(false);
  const [numericError, setNumericError] = useState(null);

  // Memoize selected field and operators
  const selectedField = useMemo(
    () => metadataFields.find(f => f.field_key === filter.field) || null,
    [metadataFields, filter.field]
  );

  const availableOperators = useMemo(
    () => selectedField
      ? getOperatorsForFieldType(selectedField.field_type, selectedField.allowed_operators)
      : [],
    [selectedField]
  );

  const requiresValue = useMemo(() => operatorRequiresValue(filter.operator), [filter.operator]);
  const requiresMultipleValues = useMemo(
    () => operatorRequiresMultipleValues(filter.operator),
    [filter.operator]
  );
  const supportsMultiSelect = useMemo(
    () => selectedField && operatorSupportsMultiSelect(filter.operator, selectedField.field_type),
    [filter.operator, selectedField]
  );

  const handleNumericPaste = (e) => {
    const pasted = e.clipboardData.getData('text').trim();
    if (pasted !== '' && isNaN(Number(pasted))) {
      e.preventDefault();
      setNumericError('Only numeric values are allowed');
      setTimeout(() => setNumericError(null), 3000);
    }
  };

  // Load lookup options for foreign_key and enum fields
  useEffect(() => {
    if (!selectedField || !requiresValue) {
      setLookupOptions([]);
      return;
    }

    const loadOptions = async () => {
      // Enum / boolean — options from metadata enum_options (database)
      if (
        (selectedField.field_type === 'enum' ||
          selectedField.field_type === 'boolean') &&
        selectedField.enum_options
      ) {
        let options = selectedField.enum_options;
        if (typeof options === 'string') {
          try {
            options = JSON.parse(options);
          } catch (e) {
            console.error('Failed to parse enum_options:', e);
            options = [];
          }
        }
        setLookupOptions(Array.isArray(options) ? options : []);
        return;
      }

      // Handle foreign_key fields — lookup URL comes only from metadata (ui_options_api).
      const isForeignKey =
        selectedField.field_type === 'foreign_key' ||
        selectedField.field_type === 'fk' ||
        selectedField.field_type === 'FK';

      const lookupUrl = isForeignKey ? selectedField.ui_options_api : null;

      if (isForeignKey && lookupUrl) {
        setLoadingLookup(true);
        try {
          const options = await FilterService.fetchLookupOptions(lookupUrl);
          setLookupOptions(options);
        } catch (error) {
          console.error('Error loading lookup options:', error);
          setLookupOptions([]);
        } finally {
          setLoadingLookup(false);
        }
      } else if (isForeignKey && !lookupUrl) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[FilterRow] FK field missing ui_options_api in metadata — set lookup_api_endpoint in system_filter_metadata:',
            selectedField.field_key,
            selectedField.fk_table
          );
        }
        setLookupOptions([]);
      } else {
        setLookupOptions([]);
      }
    };

    loadOptions();
  }, [selectedField, requiresValue, lookupContextParams]);

  // Handlers
  const handleOperatorChange = (event) => {
    const newOperator = event.target.value;
    const newRequiresValue = operatorRequiresValue(newOperator);
    const newRequiresMultiple = operatorRequiresMultipleValues(newOperator);
    const newSupportsMultiSelect = selectedField && operatorSupportsMultiSelect(newOperator, selectedField.field_type);

    // For multi-select operators (Equals on foreign_key/enum), initialize as array
    const initialValue = newRequiresValue
      ? (newRequiresMultiple || newSupportsMultiSelect ? [] : '')
      : null;

    onChange({
      ...filter,
      operator: newOperator,
      value: initialValue,
    });
  };

  const handleValueChange = (newValue, valueLabel = undefined) => {
    onChange({
      ...filter,
      value: newValue,
      ...(valueLabel !== undefined && { valueLabel }),
    });
  };

  // Render multiple values input (IN, NOT_IN, BETWEEN)
  const renderMultipleValuesInput = () => {
    const { field_type, ui_type } = selectedField;
    const currentValue = getArrayValue(filter.value);
    const isDateRangeField =
      field_type === 'date' ||
      field_type === 'datetime' ||
      ui_type === 'date';

    // Autocomplete for foreign_key/enum
    if (field_type === 'foreign_key' || field_type === 'enum') {
      const getMatchingOptions = (storedValues) => {
        const normalized = normalizeMultiSelectStoredValues(storedValues);
        if (normalized.length === 0) return [];
        return normalized.map((stored, idx) => {
          const storedStr = String(stored);
          const match =
            lookupOptions.find(opt => {
              const optVal = String(getOptionValue(opt));
              if (optVal === storedStr) return true;
              if (optVal.includes(',')) {
                return optVal.split(',').map(s => s.trim()).includes(storedStr);
              }
              return false;
            }) || null;
          if (match) return match;
          const fallbackLabel = getStoredValueLabel(filter, stored, idx);
          if (fallbackLabel) return { value: stored, label: fallbackLabel };
          return stored;
        });
      };

      return (
        <Autocomplete
          multiple
          size="small"
          fullWidth
          options={Array.isArray(lookupOptions) ? lookupOptions : []}
          getOptionLabel={getOptionLabel}
          value={getMatchingOptions(filter.value)}
          onChange={(event, newValue) => {
            const valuesToStore = newValue
              .map((opt) => getOptionValue(opt))
              .filter(
                (v) => v != null && v !== undefined && String(v) !== 'undefined' && String(v).trim() !== ''
              );
            handleValueChange(valuesToStore.length > 0 ? valuesToStore : []);
          }}
          slotProps={popperSlotProps}
          sx={autocompleteInputSx}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select values" disabled={loadingLookup} />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, idx) => (
              <Chip {...getTagProps({ index: idx })} key={idx} label={getOptionLabel(option)} sx={chipSx} />
            ))
          }
        />
      );
    }

    // Date range picker
    if (isDateRangeField) {
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <div className="ems-flt-val-pair">
            <div className="ems-flt-control">
              <span className="ems-flt-control-label">From</span>
              <DatePicker
                value={currentValue[0] || null}
                onChange={(date) => handleValueChange([date, currentValue[1]])}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: { size: 'small', fullWidth: true, sx: dateFieldSx },
                  popper: { sx: { zIndex: 10100 } },
                }}
              />
            </div>
            <div className="ems-flt-control">
              <span className="ems-flt-control-label">To</span>
              <DatePicker
                value={currentValue[1] || null}
                onChange={(date) => handleValueChange([currentValue[0], date])}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: { size: 'small', fullWidth: true, sx: dateFieldSx },
                  popper: { sx: { zIndex: 10100 } },
                }}
              />
            </div>
          </div>
        </LocalizationProvider>
      );
    }

    // Number range input
    return (
      <div className="ems-flt-val-pair">
        <div className="ems-flt-control">
          <span className="ems-flt-control-label">From</span>
          <input
            className="ems-fm-input"
            type="number"
            placeholder="0"
            value={currentValue[0] ?? ''}
            onChange={(e) => handleValueChange([e.target.value, currentValue[1]])}
            onPaste={handleNumericPaste}
          />
        </div>
        <div className="ems-flt-control">
          <span className="ems-flt-control-label">To</span>
          <input
            className="ems-fm-input"
            type="number"
            placeholder="0"
            value={currentValue[1] ?? ''}
            onChange={(e) => handleValueChange([currentValue[0], e.target.value])}
            onPaste={handleNumericPaste}
          />
        </div>
        {numericError && (
          <span style={{ fontSize: 11, color: 'var(--ems-red)' }}>{numericError}</span>
        )}
      </div>
    );
  };

  // Render single value input
  const renderSingleValueInput = () => {
    const { ui_type, field_type } = selectedField;

    // Boolean — dropdown when enum_options are configured in metadata
    if (field_type === 'boolean' && lookupOptions.length > 0) {
      const resolveBooleanOption = (stored) => {
        if (stored === null || stored === undefined || stored === '') return null;
        const storedStr = String(stored).trim();
        return (
          lookupOptions.find(
            (opt) =>
              String(opt.label).trim() === storedStr ||
              String(opt.value) === storedStr
          ) || null
        );
      };

      const selectedOption = resolveBooleanOption(filter.value);
      const selectValue = selectedOption ? String(selectedOption.label) : String(filter.value ?? '');

      return (
        <div className="ems-fm-select-wrap">
          <select
            className="ems-fm-input"
            value={selectValue}
            onChange={(e) => {
              const picked = lookupOptions.find(
                (opt) => String(opt.label) === e.target.value
              );
              const label = picked ? String(picked.label) : e.target.value;
              handleValueChange(label, label);
            }}
          >
            <option value="" disabled>Select value</option>
            {lookupOptions.map((opt) => (
              <option key={String(opt.label)} value={opt.label}>
                {typeof opt === 'object' ? opt.label : String(opt)}
              </option>
            ))}
          </select>
          <ChevronDown size={16} />
        </div>
      );
    }

    // Check if multi-select is enabled for Equals operator
    const isMultiSelect = filter.operator === OPERATORS.EQUALS && supportsMultiSelect;

    // Convert value to array if it's not already (for multi-select)
    const currentValues = isMultiSelect
      ? (Array.isArray(filter.value) ? filter.value : (filter.value !== null && filter.value !== undefined && filter.value !== '' ? [filter.value] : []))
      : null;

    // Date picker - single select only (no multi-select for dates)
    if (ui_type === 'date') {
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <DatePicker
            value={filter.value || null}
            onChange={handleValueChange}
            format="dd/MM/yyyy"
            slotProps={{
              textField: { size: 'small', fullWidth: true, sx: dateFieldSx },
              popper: { sx: { zIndex: 10100 } },
            }}
          />
        </LocalizationProvider>
      );
    }

    // Number input - multi-select support
    if (ui_type === 'number') {
      if (isMultiSelect) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            {currentValues.map((num, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  className="ems-fm-input"
                  type="number"
                  placeholder={`Value ${idx + 1}`}
                  value={num || ''}
                  onChange={(e) => {
                    const newValues = [...currentValues];
                    newValues[idx] = e.target.value;
                    handleValueChange(newValues);
                  }}
                  onPaste={handleNumericPaste}
                />
                <button
                  type="button"
                  className="ems-icon-btn"
                  onClick={() => {
                    const newValues = currentValues.filter((_, i) => i !== idx);
                    handleValueChange(newValues.length > 0 ? newValues : []);
                  }}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            {numericError && (
              <span style={{ fontSize: 11, color: 'var(--ems-red)' }}>{numericError}</span>
            )}
            <button
              type="button"
              className="ems-btn ems-sm ems-ghost"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => handleValueChange([...currentValues, ''])}
            >
              <Plus size={14} />
              Add Value
            </button>
          </div>
        );
      }
      return (
        <input
          className="ems-fm-input"
          type="number"
          placeholder="0"
          value={filter.value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          onPaste={handleNumericPaste}
        />
      );
    }

    // Boolean/Checkbox - multi-select support
    if (ui_type === 'checkbox') {
      if (isMultiSelect) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            {currentValues.map((bool, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div className="ems-fm-select-wrap" style={{ flex: 1 }}>
                  <select
                    className="ems-fm-input"
                    value={bool ?? ''}
                    onChange={(e) => {
                      const newValues = [...currentValues];
                      newValues[idx] = e.target.value === 'true';
                      handleValueChange(newValues);
                    }}
                  >
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                  <ChevronDown size={16} />
                </div>
                <button
                  type="button"
                  className="ems-icon-btn"
                  onClick={() => {
                    const newValues = currentValues.filter((_, i) => i !== idx);
                    handleValueChange(newValues.length > 0 ? newValues : []);
                  }}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="ems-btn ems-sm ems-ghost"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => handleValueChange([...currentValues, null])}
            >
              <Plus size={14} />
              Add Value
            </button>
          </div>
        );
      }
      return (
        <div className="ems-fm-select-wrap">
          <select
            className="ems-fm-input"
            value={filter.value ?? ''}
            onChange={(e) => handleValueChange(e.target.value === 'true')}
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
          <ChevronDown size={16} />
        </div>
      );
    }

    // Autocomplete for foreign_key/enum select
    if (ui_type === 'select' && (field_type === 'foreign_key' || field_type === 'enum')) {
      if (isMultiSelect) {
        // Multi-select mode for Equals operator
        const getMatchingOptions = (storedValues) => {
          const normalized = normalizeMultiSelectStoredValues(storedValues);
          if (normalized.length === 0) return [];
          return normalized.map((stored, idx) => {
            const storedStr = String(stored);
            const match =
              lookupOptions.find(opt => String(getOptionValue(opt)) === storedStr) ||
              null;
            if (match) return match;
            const fallbackLabel = getStoredValueLabel(filter, stored, idx);
            if (fallbackLabel) return { value: stored, label: fallbackLabel };
            return stored;
          });
        };
        return (
          <Autocomplete
            multiple
            size="small"
            fullWidth
            options={Array.isArray(lookupOptions) ? lookupOptions : []}
            getOptionLabel={getOptionLabel}
            value={getMatchingOptions(filter.value)}
            onChange={(event, newValue) => {
              const pairs = newValue
                .map((opt) => ({ value: getOptionValue(opt), label: getOptionLabel(opt) }))
                .filter(({ value }) => value != null && value !== undefined && String(value) !== 'undefined' && String(value).trim() !== '');
              handleValueChange(
                pairs.length > 0 ? pairs.map((p) => p.value) : [],
                pairs.length > 0 ? pairs.map((p) => p.label) : undefined
              );
            }}
            loading={loadingLookup}
            slotProps={popperSlotProps}
            sx={autocompleteInputSx}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select values" disabled={loadingLookup} />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, idx) => (
                <Chip {...getTagProps({ index: idx })} key={idx} label={getOptionLabel(option)} sx={chipSx} />
              ))
            }
          />
        );
      }

      // Single-select mode
      const findMatchingOption = (storedValue) => {
        if (storedValue == null || storedValue === '') return null;
        const storedStr = String(storedValue);
        return (
          lookupOptions.find(opt => {
            const optVal = String(getOptionValue(opt));
            if (optVal === storedStr) return true;
            if (optVal.includes(',')) {
              return optVal.split(',').map(s => s.trim()).includes(storedStr);
            }
            return false;
          }) || null
        );
      };

      return (
        <Autocomplete
          size="small"
          fullWidth
          options={Array.isArray(lookupOptions) ? lookupOptions : []}
          getOptionLabel={getOptionLabel}
          value={findMatchingOption(filter.value)}
          onChange={(event, newValue) => {
            const rawValue = newValue ? getOptionValue(newValue) : null;
            const valueToStore =
              rawValue != null && rawValue !== undefined && String(rawValue) !== 'undefined' && String(rawValue).trim() !== ''
                ? rawValue
                : null;
            const labelToStore = newValue ? getOptionLabel(newValue) : null;
            handleValueChange(valueToStore, labelToStore);
          }}
          loading={loadingLookup}
          slotProps={popperSlotProps}
          sx={autocompleteInputSx}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select value" disabled={loadingLookup} />
          )}
        />
      );
    }

    // Default text input - multi-select support
    if (isMultiSelect) {
      return (
        <Autocomplete
          multiple
          freeSolo
          size="small"
          fullWidth
          options={[]}
          value={currentValues}
          onChange={(event, newValue) => handleValueChange(newValue)}
          slotProps={popperSlotProps}
          sx={autocompleteInputSx}
          renderInput={(params) => (
            <TextField {...params} placeholder="Enter values (add multiple)" />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, idx) => (
              <Chip {...getTagProps({ index: idx })} key={idx} label={String(option)} sx={chipSx} />
            ))
          }
        />
      );
    }

    return (
      <input
        className="ems-fm-input"
        placeholder="Enter value"
        value={filter.value || ''}
        onChange={(e) => handleValueChange(e.target.value)}
      />
    );
  };

  const renderValueInput = () => {
    if (!requiresValue) {
      return (
        <div className="ems-flt-control-static">
          <span className="ems-flt-static-note">No value needed</span>
        </div>
      );
    }
    if (!selectedField) {
      return <input className="ems-fm-input" placeholder="Select field first" disabled />;
    }
    return requiresMultipleValues ? renderMultipleValuesInput() : renderSingleValueInput();
  };

  // date-range / number-range inputs render their own "From"/"To" labels; no-value
  // operators (Is Empty, Today, …) render a static note with no label either
  const suppressValueLabel = requiresMultipleValues || !requiresValue;

  return (
    <div className="ems-flt-row-main">
      {/* Field (read-only) */}
      <div className="ems-flt-control ems-flt-field">
        <span className="ems-flt-control-label">Field</span>
        <div className="ems-flt-field-val">
          <span className="ems-flt-field-ic">
            {(() => {
              const Ic = FIELD_TYPE_ICON[selectedField?.field_type] || Type;
              return <Ic size={13} />;
            })()}
          </span>
          {selectedField ? selectedField.label : filter.field || ''}
        </div>
      </div>

      {/* Operator */}
      <div className="ems-flt-control ems-flt-op">
        <span className="ems-flt-control-label">Condition</span>
        <div className="ems-fm-select-wrap">
          <select
            className="ems-fm-input"
            value={filter.operator || ''}
            onChange={handleOperatorChange}
            disabled={!selectedField || availableOperators.length === 0}
          >
            {availableOperators.length === 0 ? (
              <option value="" disabled>No operators available</option>
            ) : (
              availableOperators.map((op) => (
                <option key={op.value} value={op.value} title={op.tooltip || ''}>
                  {op.label}
                </option>
              ))
            )}
          </select>
          <ChevronDown size={16} />
        </div>
      </div>

      {/* Value */}
      <div className="ems-flt-control ems-flt-value">
        {!suppressValueLabel && <span className="ems-flt-control-label">Value</span>}
        {renderValueInput()}
      </div>

      <button className="ems-flt-del" onClick={() => onDelete(index)} aria-label="Remove filter">
        <Trash2 size={17} />
      </button>
    </div>
  );
}

export default FilterRow;
